import { useState, useEffect } from 'react';
import { MapPin, Phone, Mail, Globe, MessageCircle } from 'lucide-react';
import { apiFetch } from '../../lib/api';

interface Settings {
  contactAddress?: string;
  contactPhone?: string;
  contactEmail?: string;
  contactWhatsapp?: string;
  orgName?: string;
  orgRegNumber?: string;
  orgDescription?: string;
  orgWebsite?: string;
  receiptThreshold?: string;
}

export default function Footer() {
  const [settings, setSettings] = useState<Settings | null>(null);

  useEffect(() => {
    apiFetch<Record<string, string>>('/api/public/settings').then((res: { success: boolean; data?: Record<string, string> }) => {
      if (res.success && res.data) {
        setSettings({
          contactAddress: res.data.contactAddress,
          contactPhone: res.data.contactPhone,
          contactEmail: res.data.contactEmail,
          contactWhatsapp: res.data.contactWhatsapp,
          orgName: res.data.orgName,
          orgRegNumber: res.data.orgRegNumber,
          orgDescription: res.data.orgDescription,
          orgWebsite: res.data.orgWebsite,
          receiptThreshold: res.data.receiptThreshold,
        });
      }
    });
  }, []);

  return (
    <footer className="bg-gradient-to-b from-gray-900 to-gray-950 text-white">
      <div className="container-max mx-auto px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {/* Brand */}
          <div className="md:col-span-1">
            <div className="flex items-center gap-3 mb-4">
              <h2 className="text-xl font-bold text-white">
                {settings?.orgName || '好餸傳愛．社區飯堂'}
              </h2>
            </div>
            {settings?.orgDescription && (
              <p className="text-gray-400 text-sm leading-relaxed mb-4">
                {settings.orgDescription}
              </p>
            )}
            <div className="mt-3">
              <img
                src="https://s2c-donation-api.jimsbond007.workers.dev/api/public/media/general%2F1777627051714_z34l4y1y.png"
                alt="慈善機構註冊"
                className="h-24 w-auto object-contain"
                loading="lazy"
              />
            </div>
          </div>

          {/* Contact */}
          <div>
            <h3 className="font-bold text-lg mb-4 text-white">聯絡我們</h3>
            <div className="space-y-3 text-gray-400 text-sm">
              {settings?.contactAddress && (
                <div className="flex items-start gap-2">
                  <MapPin className="w-4 h-4 text-orange-400 mt-0.5 flex-shrink-0" />
                  <span>{settings.contactAddress}</span>
                </div>
              )}
              {settings?.contactPhone && (
                <a
                  href={`tel:${settings.contactPhone.replace(/\s/g, '')}`}
                  className="flex items-center gap-2 hover:text-orange-400 transition-colors"
                >
                  <Phone className="w-4 h-4 text-orange-400" />
                  <span>{settings.contactPhone}</span>
                </a>
              )}
              {settings?.contactWhatsapp && (
                <a
                  href={`https://wa.me/${settings.contactWhatsapp.replace(/[^0-9]/g, '')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 hover:text-orange-400 transition-colors"
                >
                  <MessageCircle className="w-4 h-4 text-orange-400" />
                  <span>WhatsApp</span>
                </a>
              )}
              {settings?.contactEmail && (
                <a
                  href={`mailto:${settings.contactEmail}`}
                  className="flex items-center gap-2 hover:text-orange-400 transition-colors"
                >
                  <Mail className="w-4 h-4 text-orange-400" />
                  <span>{settings.contactEmail}</span>
                </a>
              )}
              {settings?.orgWebsite && (
                <a
                  href={settings.orgWebsite}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 hover:text-orange-400 transition-colors"
                >
                  <Globe className="w-4 h-4 text-orange-400" />
                  <span>{settings.orgWebsite.replace(/^https?:\/\//, '')}</span>
                </a>
              )}
            </div>
          </div>

          {/* About */}
          <div>
            <h3 className="font-bold text-lg mb-4 text-white">關於</h3>
            <div className="space-y-2 text-gray-400 text-sm">
              {settings?.orgName && (
                <p>{settings.orgName}為註冊慈善機構</p>
              )}
              {settings?.orgRegNumber && (
                <p>稅務檔案號碼：{settings.orgRegNumber}</p>
              )}
              {settings?.receiptThreshold && (
                <p>捐款 {settings.receiptThreshold} 或以上可獲扣稅收據</p>
              )}
              <div className="pt-2 mt-2 border-t border-gray-800">
                <p className="text-xs text-gray-500">From Opportunities to Impact</p>
                <p className="text-xs text-gray-500">扶助 SEN 青年成就職夢</p>
              </div>
            </div>
          </div>

          {/* Partners */}
          <div>
            <h3 className="font-bold text-lg mb-4 text-white">支持機構</h3>
            <div className="space-y-2">
              {['獅球嘜', '道慈佛社楊日霖紀念學校', 'Big Bang Marketing'].map((name) => (
                <span
                  key={name}
                  className="inline-block px-3 py-1.5 bg-gray-800 rounded-lg text-xs text-gray-400 border border-gray-700"
                >
                  {name}
                </span>
              ))}
            </div>
            <div className="mt-4 pt-4 border-t border-gray-800">
              <p className="text-xs text-gray-500 mb-2">媒體報導</p>
              <a
                href="https://ngo.ctgoodjobs.hk/article/1327-32399/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-orange-400 hover:text-orange-300 transition-colors"
              >
                CTgoodjobs NGO 專訪 →
              </a>
            </div>
          </div>
        </div>

        <div className="mt-10 pt-6 border-t border-gray-800 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="text-gray-500 text-xs text-center sm:text-left">
            © {new Date().getFullYear()} {settings?.orgName || '好餸傳愛．社區飯堂'} 版權所有
          </div>
          <a
            href="https://jkdcoding.com"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 opacity-60 hover:opacity-100 transition-opacity"
          >
            <span className="text-xs text-gray-500">Powered by <span className="font-bold" style={{ color: '#FFFF00', textShadow: '0 0 8px rgba(255,255,0,0.6)' }}>JKDCoding</span></span>
          </a>
          <div className="flex items-center gap-4 text-xs text-gray-600">
            <a href="/privacy" className="hover:text-gray-400 transition-colors">隱私政策</a>
            <span>|</span>
            <a href="/terms" className="hover:text-gray-400 transition-colors">使用條款</a>
          </div>
        </div>
      </div>
    </footer>
  );
}
